// Stretch-to-tempo verb — auto-detect the source's BPM, then time-stretch
// it to a target BPM (without changing pitch — the granular engine
// preserves frequency content while compressing/expanding time).
//
// Pipeline: source picked → tempoDetect → user dials target BPM →
// applyBlock({stretch, factor}) where factor = source_bpm / target_bpm.

import { useCallback, useEffect, useState } from 'react';
import { applyBlock } from './mangleEffects';
import { detectTempo, type TempoEstimate } from './tempoDetect';
import { fromPcm, toMono, toPcm } from '../../pipeline';
import { Slider } from '../Slider';
import { SourcePicker, type PickedSource } from '../SourcePicker';
import type { VerbProps } from '../types';

export function StretchVerb({
  audioContext,
  storage,
  onBuffer,
  onStatus,
}: VerbProps) {
  const [source, setSource] = useState<PickedSource | null>(null);
  const [detected, setDetected] = useState<TempoEstimate | null>(null);
  const [targetBpm, setTargetBpm] = useState(120);
  const [grainMs, setGrainMs] = useState(80);
  const [rendering, setRendering] = useState(false);

  // Re-detect whenever the source changes. Mono-sum first because
  // tempoDetect operates on a single channel and a hard-panned stereo
  // loop could give different BPMs depending on which channel won.
  useEffect(() => {
    if (!source) {
      setDetected(null);
      return;
    }
    const mono = toMono(toPcm(source.buffer)).channels[0]!;
    const result = detectTempo(mono, source.buffer.sampleRate);
    setDetected(result);
    if (result) setTargetBpm(Math.round(result.bpm));
  }, [source]);

  const handleRender = useCallback(async () => {
    if (!source || !detected || !audioContext) return;
    if (detected.bpm <= 0 || targetBpm <= 0) return;
    const factor = detected.bpm / targetBpm;
    setRendering(true);
    try {
      const result = await applyBlock(
        toPcm(source.buffer),
        { kind: 'stretch', factor, grainMs, overlap: 0.5 },
        { ctx: audioContext, storage },
      );
      const buffer = fromPcm(audioContext, result);
      const sourceIds = source.sampleId ? [source.sampleId] : [];
      onBuffer(buffer, {
        suggestedName: `${source.name}_${Math.round(targetBpm)}bpm`,
        origin: {
          kind: 'stretch-to-tempo',
          sourceSampleId: source.sampleId,
          sourceBpm: detected.bpm,
          targetBpm,
          grainMs,
        },
        sourceSampleIds: sourceIds.length > 0 ? sourceIds : undefined,
      });
      onStatus('Rendered — trim and save.', 'ok');
    } catch (e) {
      onStatus(
        'Render failed: ' + (e instanceof Error ? e.message : String(e)),
        'err',
      );
    } finally {
      setRendering(false);
    }
  }, [
    source,
    detected,
    audioContext,
    storage,
    targetBpm,
    grainMs,
    onBuffer,
    onStatus,
  ]);

  return (
    <div className="flex flex-col gap-3 min-h-[200px]">
      <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-400">
        Detect the source's tempo and stretch it to a target BPM. Pitch is
        preserved (granular time-stretch).
      </div>
      <SourcePicker
        audioContext={audioContext}
        selectedId={source?.sampleId}
        onPicked={setSource}
        onStatus={onStatus}
        label="Source loop"
      />
      {source && (
        <div className="bg-zinc-950 border border-zinc-800 px-3 py-2 text-[11px] text-zinc-300 flex items-center justify-between gap-2">
          <span>Detected</span>
          {detected ? (
            <div className="flex items-center gap-2">
              <span className="tabular-nums">
                {detected.bpm.toFixed(1)} BPM ·{' '}
                <span className="text-zinc-500">
                  conf {(detected.confidence * 100).toFixed(0)}%
                </span>
              </span>
              {/* Octave-correction: autocorrelation is honest about the
                  dominant period but humans pick downbeats, so a ÷2 / ×2
                  fixes the common "detected as double-time" case. */}
              <button
                type="button"
                onClick={() => {
                  setDetected({ ...detected, bpm: detected.bpm / 2 });
                  // Snap target to match — most common intent is "the
                  // detection was wrong, leave the source alone."
                  setTargetBpm(Math.round(detected.bpm / 2));
                }}
                className="px-1.5 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-zinc-900 border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                title="Halve detected BPM"
              >
                ÷2
              </button>
              <button
                type="button"
                onClick={() => {
                  setDetected({ ...detected, bpm: detected.bpm * 2 });
                  setTargetBpm(Math.round(detected.bpm * 2));
                }}
                className="px-1.5 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-zinc-900 border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                title="Double detected BPM"
              >
                ×2
              </button>
            </div>
          ) : (
            <span className="text-zinc-500 italic">no clear tempo</span>
          )}
        </div>
      )}
      <Slider
        label="Target"
        value={targetBpm}
        min={40}
        max={240}
        step={1}
        display={`${targetBpm} BPM`}
        onChange={setTargetBpm}
        displayWidth="narrow"
      />
      <Slider
        label="Grain"
        value={grainMs}
        min={20}
        max={300}
        step={5}
        display={`${grainMs} ms`}
        onChange={setGrainMs}
        displayWidth="narrow"
      />
      <button
        type="button"
        onClick={() => void handleRender()}
        disabled={!source || !detected || rendering}
        className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30"
      >
        {rendering ? 'Rendering…' : '● Render'}
      </button>
    </div>
  );
}

